import i18next from "i18next";
import { parseEnvText } from "./envText";

/**
 * One variable as the create form holds it, before anything is sent.
 *
 * `id` is for React keys and focus, and is never sent: rows are added,
 * removed and reordered by paste, and a key-based identity would lose the
 * cursor every time somebody typed a name. `secretTouched` records that the
 * customer set the lock themselves, which is when the name-based guess stops.
 */
export interface EnvRow {
  id: number;
  key: string;
  value: string;
  isSecret: boolean;
  secretTouched: boolean;
}

let seq = 0;
export function nextRowId(): number {
  seq += 1;
  return seq;
}

export function emptyRow(): EnvRow {
  return { id: nextRowId(), key: "", value: "", isSecret: false, secretTouched: false };
}

/** What the API accepts as a name: a shell identifier. */
export const ENV_KEY_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

/**
 * Names that almost always hold a credential, and values that carry one — a
 * URL with a password before the host. A default, not a decision: the lock on
 * the row is right there, and once it has been touched this is not consulted.
 */
const SECRET_NAMES =
  /(SECRET|TOKEN|PASSWORD|PASSWD|PRIVATE|CREDENTIAL|API_?KEY|ACCESS_KEY|SIGNING|_DSN|^DSN|_AUTH|^AUTH_)/i;
const URL_WITH_PASSWORD = /^[a-z][a-z0-9+.-]*:\/\/[^\s/@]+:[^\s/@]+@/i;

export function looksSecret(key: string, value: string): boolean {
  return SECRET_NAMES.test(key.trim()) || URL_WITH_PASSWORD.test(value.trim());
}

/** Rows from a pasted `.env`: parsed the same way the bulk import is, secrets guessed. */
export function rowsFromText(text: string): { rows: EnvRow[]; invalidLines: number[] } {
  const { vars, invalidLines } = parseEnvText(text);
  return {
    rows: vars.map(({ key, value }) => ({
      id: nextRowId(),
      key,
      value,
      isSecret: looksSecret(key, value),
      secretTouched: false,
    })),
    invalidLines,
  };
}

/**
 * Merges pasted rows into the list.
 *
 * A key that already exists is updated where it is, keeping a lock the
 * customer set by hand; new keys land at `at` — in place of the row the paste
 * happened in when that row is blank, after it otherwise — so a paste into
 * the empty first row fills the list top to bottom rather than leaving a
 * blank line above it. The list never comes back empty.
 */
export function mergeRows(current: EnvRow[], incoming: EnvRow[], at: number = current.length): EnvRow[] {
  const keyOf = (row: EnvRow) => row.key.trim();
  const byKey = new Map(incoming.map((row) => [row.key, row]));

  const updated = current.map((row) => {
    const hit = keyOf(row) ? byKey.get(keyOf(row)) : undefined;
    if (!hit) return row;
    return { ...row, value: hit.value, isSecret: row.secretTouched ? row.isSecret : hit.isSecret };
  });

  const present = new Set(current.map(keyOf).filter(Boolean));
  const fresh = incoming.filter((row) => !present.has(row.key));

  const index = Math.min(at, updated.length);
  const target = updated[index];
  const replaceTarget = target !== undefined && keyOf(target) === "" && target.value === "";
  const rows = replaceTarget
    ? [...updated.slice(0, index), ...fresh, ...updated.slice(index + 1)]
    : [...updated.slice(0, index + 1), ...fresh, ...updated.slice(index + 1)];

  return rows.length > 0 ? rows : [emptyRow()];
}

export interface EnvProblem {
  id: number;
  message: string;
}

/**
 * What stops the form: a name the API would refuse, a name set twice, a value
 * with no name. Blank rows are not problems — one is always on screen.
 */
const createT = () => i18next.getFixedT(null, "create");

export function envProblems(rows: EnvRow[]): EnvProblem[] {
  const t = createT();
  const problems: EnvProblem[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) {
      if (row.value !== "") problems.push({ id: row.id, message: t("env.problem.noName") });
      continue;
    }
    if (!ENV_KEY_PATTERN.test(key)) {
      problems.push({
        id: row.id,
        message: t("env.problem.badKey", { key }),
      });
      continue;
    }
    if (seen.has(key)) {
      problems.push({ id: row.id, message: t("env.problem.duplicate", { key }) });
      continue;
    }
    seen.add(key);
  }
  return problems;
}

/** What is sent: named rows only, names trimmed. */
export function envPayload(rows: EnvRow[]): { key: string; value: string; isSecret: boolean }[] {
  return rows
    .filter((row) => row.key.trim() !== "")
    .map((row) => ({ key: row.key.trim(), value: row.value, isSecret: row.isSecret }));
}

export function summarizeEnv(rows: EnvRow[]): { count: number; secrets: number } {
  const set = envPayload(rows);
  return { count: set.length, secrets: set.filter((row) => row.isSecret).length };
}
